import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Bone, Heart, Zap, ShieldCheck, ArrowRight, Star, Sparkles, Award, Leaf, TestTube, Package, Puzzle, Filter, Info } from 'lucide-react';
import { Button } from '../ui/Button';
import { ProductCard } from '../ProductCard';
import { products } from '../../data/products';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Badge } from '../ui/Badge';
import { Tooltip } from '../ui/Tooltip';
// Types
type CategoryId = 'all' | 'interactive' | 'chew' | 'plush' | 'training';
export function ToysShowcase() {
  const [activeCategory, setActiveCategory] = useState<CategoryId>('interactive');
  // Filter products
  const categoryProducts = products.filter(p => p.category === 'toys' && (activeCategory === 'all' || p.subcategory === activeCategory)).slice(0, 3);
  const categories = [{
    id: 'interactive',
    label: 'Mente Activa',
    icon: Brain,
    color: 'text-purple-600',
    bg: 'bg-purple-50'
  }, {
    id: 'chew',
    label: 'Salud Dental',
    icon: Bone,
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  }, {
    id: 'plush',
    label: 'Confort',
    icon: Heart,
    color: 'text-rose-600',
    bg: 'bg-rose-50'
  }, {
    id: 'training',
    label: 'Entrenamiento',
    icon: Zap,
    color: 'text-amber-600',
    bg: 'bg-amber-50'
  }];
  return <section className="py-24 lg:py-32 bg-white relative overflow-hidden" data-id="element-3562">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-purple-50/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-3563" />

      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-3564">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16" data-id="element-3565">
          <div className="max-w-2xl" data-id="element-3566">
            <EnhancedScrollReveal data-id="element-3567">
              <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-200 mb-6" data-id="element-3568">
                Enriquecimiento Ambiental
              </Badge>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal-900 mb-6 leading-tight" data-id="element-3569">
                Juguetes diseñados para <br data-id="element-3570" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600" data-id="element-3571">
                  mentes brillantes
                </span>
              </h2>
              <p className="text-lg text-charcoal-600 leading-relaxed font-light" data-id="element-3572">
                Herramientas de bienestar seleccionadas por etólogos. Porque un
                perro aburrido es un perro infeliz.
              </p>
            </EnhancedScrollReveal>
          </div>

          <EnhancedScrollReveal delay={0.2} data-id="element-3573">
            <Link to="/productos?category=toys" data-id="element-3574">
              <Button variant="outline" className="rounded-full border-charcoal-200 hover:bg-charcoal-50" data-id="element-3575">
                Ver Colección Completa <ArrowRight className="ml-2 w-4 h-4" data-id="element-3576" />
              </Button>
            </Link>
          </EnhancedScrollReveal>
        </div>

        {/* Interactive Showcase Layout */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-3577">
          {/* Sidebar: Categories & Science */}
          <div className="lg:col-span-3 space-y-8" data-id="element-3578">
            <div className="space-y-2" data-id="element-3579">
              <h3 className="text-xs font-bold text-charcoal-400 uppercase tracking-wider mb-4 px-2" data-id="element-3580">
                Categorías
              </h3>
              {categories.map(cat => <button key={cat.id} onClick={() => setActiveCategory(cat.id as CategoryId)} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-300 ${activeCategory === cat.id ? 'bg-charcoal-900 text-white shadow-lg scale-105' : 'bg-white text-charcoal-600 hover:bg-gray-50'}`} data-id="element-3581">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeCategory === cat.id ? 'bg-white/20' : cat.bg}`} data-id="element-3582">
                    <cat.icon className={`w-4 h-4 ${activeCategory === cat.id ? 'text-white' : cat.color}`} data-id="element-3583" />
                  </div>
                  <span className="font-bold text-sm" data-id="element-3584">{cat.label}</span>
                </button>)}
            </div>

            {/* Science Card */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-100" data-id="element-3585">
              <div className="flex items-center gap-2 mb-4" data-id="element-3586">
                <Brain className="w-5 h-5 text-purple-600" data-id="element-3587" />
                <span className="text-xs font-bold text-purple-700 uppercase" data-id="element-3588">
                  Dato Científico
                </span>
              </div>
              <p className="text-sm text-charcoal-700 leading-relaxed mb-4" data-id="element-3589">
                "Resolver un puzzle de nivel medio durante 15 minutos consume
                tantas calorías cognitivas como una caminata de 45 minutos."
              </p>
              <div className="flex items-center gap-2 text-xs text-charcoal-500 font-medium" data-id="element-3590">
                <ShieldCheck className="w-3 h-3" data-id="element-3591" />
                <span data-id="element-3592">Verificado por Etólogos</span>
              </div>
            </div>
          </div>

          {/* Main Content: Products Grid */}
          <div className="lg:col-span-9" data-id="element-3593">
            <AnimatePresence mode="wait" data-id="element-3594">
              <motion.div key={activeCategory} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="grid md:grid-cols-3 gap-6" data-id="element-3595">
                {/* Featured Large Card */}
                {categoryProducts[0] && <div className="md:col-span-2 relative group" data-id="element-3596">
                    <div className="h-full bg-white rounded-3xl p-8 border border-gray-100 shadow-soft-xl hover:shadow-soft-2xl transition-all duration-500 flex flex-col relative overflow-hidden" data-id="element-3597">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-[100px] -z-0" data-id="element-3598" />

                      <div className="relative z-10 flex justify-between items-start mb-6" data-id="element-3599">
                        <Badge variant="primary" className="shadow-md" data-id="element-3600">
                          Recomendado
                        </Badge>
                        <Tooltip content="Nivel de dificultad mental" data-id="element-3601">
                          <div className="flex gap-1" data-id="element-3602">
                            {[1, 2, 3].map(i => <div key={i} className={`w-2 h-6 rounded-full ${i <= 2 ? 'bg-purple-500' : 'bg-gray-200'}`} data-id="element-3603" />)}
                          </div>
                        </Tooltip>
                      </div>

                      <div className="flex-1 flex items-center justify-center mb-8 group-hover:scale-105 transition-transform duration-500" data-id="element-3604">
                        <img src={categoryProducts[0].image} alt={categoryProducts[0].name} className="w-64 h-64 object-contain drop-shadow-xl" data-id="element-3605" />
                      </div>

                      <div data-id="element-3606">
                        <h3 className="text-2xl font-bold text-charcoal-900 mb-2 font-serif" data-id="element-3607">
                          {categoryProducts[0].name}
                        </h3>
                        <p className="text-charcoal-500 mb-6 line-clamp-2" data-id="element-3608">
                          {categoryProducts[0].description}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-3609">
                          <span className="text-xl font-bold text-charcoal-900" data-id="element-3610">
                            {categoryProducts[0].price}€
                          </span>
                          <Button className="rounded-full" data-id="element-3611">
                            Añadir al Carrito
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Secondary Cards */}
                <div className="flex flex-col gap-6" data-id="element-3612">
                  {categoryProducts.slice(1).map(product => <ProductCard key={product.id} product={product} data-id="element-3613" />)}

                  {/* Explore More Card */}
                  <Link to={`/productos?category=toys`} className="flex-1 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center p-6 text-center hover:bg-gray-100 hover:border-gray-300 transition-all group" data-id="element-3614">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm mb-3 group-hover:scale-110 transition-transform" data-id="element-3615">
                      <ArrowRight className="w-5 h-5 text-charcoal-400" data-id="element-3616" />
                    </div>
                    <span className="font-bold text-charcoal-600" data-id="element-3617">
                      Ver todos los juguetes
                    </span>
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}